import TableOnly from "components/DataTable/TableOnly";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import PaginationWithState from "components/Pagination/PaginationWithState";
import { truncateString } from "utils/UtilityFuncs";

// approved (user, 2026-10-29): the Approved tab -- the same list, only for
// work orders with the Approved status.
export default function Pending({ approved = false } = {}) {
  const dispatch = useDispatch();

  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [pendingData, setPendingData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [productTypes, setProductTypes] = useState([]);
  const [selectedProductTypeId, setSelectedProductTypeId] = useState(null);

  const [locationsList, setLocationsList] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  /*   All Functions
   ********************************************* */
  const getPendingData = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
            ? query
            : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          `/sales/purchasing/${approved ? "approved" : "pending"}?page=` +
            usedPage +
            "&limit=" +
            pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
            productTypeId: selectedProductTypeId || undefined,
            locationId: selectedLocationId || undefined,
          },
          { headers: authHeader() },
        );

        if (data?.success) {
          setPendingData(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            }),
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [
      approved,
      pageNumber,
      pageLimit,
      filterBy.value,
      selectedProductTypeId,
      selectedLocationId,
    ],
  );

  const updatePurchaseStatus = async (item, { purchased, received }) => {
    const nextPurchased =
      purchased !== undefined ? purchased : !!item.purchased;
    const nextReceived = received !== undefined ? received : !!item.received;

    try {
      setLoading(true);
      const { data } = await API.patch(
        `/wo-products/purchase-status`,
        {
          id: item.id,
          purchased: nextPurchased,
          received: nextReceived,
        },
        { headers: authHeader() },
      );

      if (data?.success) {
        setPendingData((prev) =>
          prev.map((p) =>
            p.id === item.id
              ? { ...p, purchased: nextPurchased, received: nextReceived }
              : p,
          ),
        );
        dispatch(setNotification({ message: data.message, type: "success" }));
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to update",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const getProductTypes = async () => {
    try {
      const { data } = await API.post(
        "sales/products/product-types",
        { page: 1, limit: 100, filterBy: "all" },
        { headers: authHeader() },
      );
      if (data?.success) {
        setProductTypes(data.data || []);
      }
    } catch (err) {
      // silently fail — tabs just won't appear
    }
  };

  const getAllLocations = async () => {
    try {
      const { data } = await API.get("/locations", { headers: authHeader() });
      if (data) setLocationsList(data);
    } catch (err) {
      // silently fail
    }
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getPendingData(
        searchTerm && searchTerm !== ""
          ? { query: searchTerm, page: pageNumber }
          : { clearSearch: true, page: pageNumber },
      );
    }, 50);
    return () => clearTimeout(timeout);
  }, [
    pageNumber,
    pageLimit,
    filterBy,
    selectedProductTypeId,
    selectedLocationId,
  ]);

  useEffect(() => {
    getProductTypes();
    getAllLocations();
  }, []);

  /*   Table
   ********************************************* */
  let filters = [
    { value: "woId", name: "W-ID" },
    { value: "customerName", name: "Customer Name" },
    { value: "productType", name: "Product Type" },
    { value: "styleNo", name: "Style No" },
    { value: "color", name: "Color" },
    { value: "teamName", name: "Team Name" },
  ];

  const table_head = [
    { name: "W-ID", value: "woId" },
    { name: "Customer Name", value: "customerName" },
    { name: "Product Type", value: "productType" },
    { name: "Style No", value: "styleNo" },
    { name: "Color", value: "color" },
    { name: "Team Name", value: "teamName" },
    { name: "Purchased", value: "purchased" },
    { name: "Received", value: "received" },
  ];

  const table_body = (item, index) => (
    <tr key={item.id}>
      <td className="align-middle py-1">
        <Link
          to={`/work-order/details/all/${item.WorkOrder?.woId}?page=1`}
          state={{
            workOrderIds: [
              ...new Set(
                pendingData?.map((a) => a?.WorkOrder?.woId).filter(Boolean),
              ),
            ],
          }}
          className="btn btn-sm my-1 py-0 px-1"
        >
          <span className="text-nowrap">W-{item.WorkOrder?.woId}</span>
        </Link>
      </td>
      <td className="align-middle py-1">
        <Link
          to={`/customer/details/${item?.WorkOrder?.customer?.customerId}`}
          className="btn btn-sm my-1 py-0 px-1"
        >
          {truncateString(item?.WorkOrder?.customerName, 35) || "-"}
        </Link>
      </td>
      <td className="align-middle py-1">{item?.productTypes?.productTypes}</td>
      <td className="align-middle py-1">{item?.productStyle}</td>
      <td className="align-middle py-1">{item?.productColor}</td>
      <td className="align-middle py-1">{item?.teamName}</td>

      {/*   Purchased Toggle
       ********************************************* */}
      <td className="align-middle py-1">
        <div className="d-flex align-items-center gap-1">
          <input
            onChange={(e) => {
              e.preventDefault();
              e.stopPropagation();
              updatePurchaseStatus(item, { purchased: e.target.checked });
            }}
            checked={!!item.purchased}
            type="checkbox"
            id={`switch-purchased-${item.id}`}
            data-switch="success"
          />
          <label
            htmlFor={`switch-purchased-${item.id}`}
            data-on-label="Yes"
            data-off-label="No"
            className="mb-0"
          ></label>
        </div>
      </td>

      {/*   Received Toggle
       ********************************************* */}
      <td className="align-middle py-1">
        <div className="d-flex align-items-center gap-1">
          <input
            onChange={(e) => {
              e.preventDefault();
              e.stopPropagation();
              updatePurchaseStatus(item, { received: e.target.checked });
            }}
            checked={!!item.received}
            type="checkbox"
            id={`switch-received-${item.id}`}
            data-switch="success"
          />
          <label
            htmlFor={`switch-received-${item.id}`}
            data-on-label="Yes"
            data-off-label="No"
            className="mb-0"
          ></label>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="p-2 pt-0">
      {loading && <Spinner />}

      {/*   Product Type Tabs
       ********************************************* */}
      <div className="btn-group mb-2">
        <button
          className={`btn btn-sm ${
            selectedProductTypeId === null
              ? "btn-primary"
              : "btn-outline-primary"
          }`}
          onClick={() => {
            setSelectedProductTypeId(null);
            setPageNumber(1);
          }}
        >
          All
        </button>
        {productTypes.map((pt) => (
          <button
            key={pt.id}
            className={`btn btn-sm ${
              selectedProductTypeId === pt.productTypeId
                ? "btn-primary"
                : "btn-outline-primary"
            }`}
            onClick={() => {
              setSelectedProductTypeId(pt.productTypeId);
              setPageNumber(1);
            }}
          >
            {pt.productTypes}
          </button>
        ))}
      </div>

      {/*   Table Controls
       ********************************************* */}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex align-items-center gap-1">
          {/* Location */}
          <select
            className="form-select form-select-sm me-1"
            style={{ maxWidth: 160 }}
            value={selectedLocationId ?? ""}
            onChange={(e) => {
              setSelectedLocationId(
                e.target.value ? Number(e.target.value) : null,
              );
              setPageNumber(1);
            }}
          >
            <option value="">All Locations</option>
            {locationsList?.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.locationName}
              </option>
            ))}
          </select>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getPendingData({ query: searchTerm, page: 1 });
              } else {
                getPendingData({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getPendingData({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 d-flex"
        style={{ minHeight: "50vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={pendingData}
            disableSorting={true}
            actionsOnEnd={false}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>
    </div>
  );
}
